import React, { useRef, useState } from "react";
import { Card, Button } from "react-bootstrap";
import GoogleMapReact from 'google-map-react';
import Settings from "@/common/Settings";
import PinComponent from "@/components/Common/PinComponent";
import LeafletSingleMap from "@/components/core/LeafletSingleMap";
import { useVisitsPreferences } from "@/hooks/useLocalStorage";
import { useTranslation } from "react-i18next";

class GoogleMapSafeWrapper extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.warn("Google Maps failed to load or render, falling back to Leaflet:", error);
  }

  render() {
    if (this.state.hasError) {
      return <LeafletSingleMap model={this.props.model} />;
    }
    return this.props.children;
  }
}

const VisitMap = (props) => {
  const { t } = useTranslation();
  const model = props.model || {};
  const { mapProvider } = useVisitsPreferences();
  const googleMapRef = useRef(null);
  const zoom = 19;

  const lat = Number(model.latitude);
  const lng = Number(model.longitude);
  const hasValidCoords = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;

  const getFullAddress = (item) => {
    let address = item.address || "";
    if (item.address2) address += ", " + item.address2;
    if (item.sector) address += ", " + item.sector;
    if (item.city) address += ", " + item.city;
    if (item.region) address += ", " + item.region;
    if (item.country) address += ", " + item.country;
    return address;
  };

  if (mapProvider === "osm") {
    return <LeafletSingleMap model={model} />;
  }

  if (!hasValidCoords) {
    return (
      <Card className="shadow-sm border rounded-3 overflow-hidden">
        <Card.Header className="bg-white py-2 px-3 d-flex align-items-center justify-content-between border-bottom">
          <h6 className="fw-bold mb-0 text-dark">
            <i className="fas fa-map-location-dot me-2 text-primary" />
            {t("Ubicación en Mapa")}
          </h6>
        </Card.Header>
        <Card.Body className="text-muted text-center py-4">
          <i className="fas fa-map-location-dot me-2 text-secondary fs-4 d-block mb-2" />
          {t("Sin coordenadas registradas")}
        </Card.Body>
        <Card.Footer className="bg-light py-2 px-3 text-muted small border-top">
          <i className="fas fa-location-dot me-2 text-danger" />
          {getFullAddress(model) || t("Sin dirección registrada")}
        </Card.Footer>
      </Card>
    );
  }

  return (
    <GoogleMapSafeWrapper model={model}>
      <Card className="shadow-sm border rounded-3 overflow-hidden">
        <Card.Header className="bg-white py-2 px-3 d-flex align-items-center justify-content-between border-bottom">
          <h6 className="fw-bold mb-0 text-dark">
            <i className="fas fa-map-location-dot me-2 text-primary" />
            {t("Ubicación en Mapa")}
          </h6>
          <div className="d-flex gap-1">
            <Button
              variant="outline-primary"
              size="sm"
              className="py-1 px-2 rounded-3"
              style={{ fontSize: "0.78rem" }}
              onClick={() => {
                const fullAddress = `${lat},${lng}`;
                window.open(
                  `https://www.google.com/maps/search/?api=1&query=${fullAddress}`,
                  "MapWindow",
                  "width=1280,height=840"
                );
              }}
            >
              <i className="fas fa-up-right-from-square me-1" />
              {t("Abrir en Google Maps")}
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0" style={{ height: "340px", position: "relative" }}>
          <GoogleMapReact
            ref={googleMapRef}
            yesIWantToUseGoogleMapApiInternals={true}
            bootstrapURLKeys={{ key: Settings.GoogleMapsKey }}
            style={{ width: "100%", height: "100%", position: "relative" }}
            center={{ lat, lng }}
            defaultZoom={zoom}
          >
            <PinComponent
              Type={"Visit"}
              lat={lat}
              item={model}
              lng={lng}
              text={model.name}
            />
          </GoogleMapReact>
        </Card.Body>
        <Card.Footer className="bg-light py-2 px-3 text-muted small border-top">
          <i className="fas fa-location-dot me-2 text-danger" />
          {getFullAddress(model) || t("Sin dirección registrada")}
        </Card.Footer>
      </Card>
    </GoogleMapSafeWrapper>
  );
};

export default VisitMap;
